/**
 * AppICEMAP — Category styling
 * One place deciding what colour and glyph a category gets, so the map marker,
 * the pills, the report cards and the "Nuevo reporte" picker always agree.
 *
 * Directus only stores `color_ramp` (all accent variants) and a Phosphor icon
 * name, which made every category look the same shade of lavender. The palette
 * below overrides that per slug; unknown slugs fall back to the Directus data.
 *
 * Icons are inline SVG (24×24, currentColor) so they inherit `ink` and need no
 * network request or icon font.
 */

/* ── Glyphs ──────────────────────────────────────────────────────────────── */

/** Ice cube — isometric block with a highlight */
const ICON_ICE = `
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path d="M12 2.2 21 7v10l-9 4.8L3 17V7l9-4.8Z" opacity=".28"/>
  <path d="M12 2.2 21 7l-9 4.9L3 7l9-4.8Z"/>
  <path d="M3 7v10l9 4.8v-9.9L3 7Z" opacity=".72"/>
  <path d="M6.6 9.1 9 10.4v3.1L6.6 12.2V9.1Z" fill="#fff" opacity=".55"/>
</svg>`;

/** Police cap — crown, brim and badge */
const ICON_POLICE = `
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path d="M12 3.4c-4 0-7.2 2.9-7.6 6.7l-.1 1.4h15.4l-.1-1.4C19.2 6.3 16 3.4 12 3.4Z"/>
  <path d="M12 5.6l1.2 2.5 2.7.4-2 1.9.5 2.7-2.4-1.3-2.4 1.3.5-2.7-2-1.9 2.7-.4L12 5.6Z" fill="#fff" opacity=".85"/>
  <path d="M3 13h18a1.6 1.6 0 0 1 1.6 1.6v1.2A1.6 1.6 0 0 1 21 17.4H3a1.6 1.6 0 0 1-1.6-1.6v-1.2A1.6 1.6 0 0 1 3 13Z" opacity=".78"/>
</svg>`;

/** Alert — exclamation in a disc */
const ICON_SAFETY = `
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path d="M12 2.4a9.6 9.6 0 1 0 0 19.2 9.6 9.6 0 0 0 0-19.2Zm0 3.4c.8 0 1.4.7 1.3 1.5l-.5 5.4a.9.9 0 0 1-1.7 0l-.5-5.4A1.4 1.4 0 0 1 12 5.8Zm0 10a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"/>
</svg>`;

/** Reflective warning triangle on its stand */
const ICON_ACCIDENT = `
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path fill-rule="evenodd" d="M12 2.6 22.2 19H1.8L12 2.6Zm0 4.6L6.1 16.7h11.8L12 7.2Z"/>
  <path d="M6.4 19.6h11.2v1.8H6.4z" opacity=".7"/>
  <path d="M12 9.6l3.5 5.7h-7L12 9.6Z" opacity=".45"/>
</svg>`;

/** Pothole — ragged hole broken into the asphalt */
const ICON_POTHOLE = `
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path d="M9.3 4.9 11 7.6l2.6-1.4-.7 2.6 2.9.6-2.2 1.6 1.6 2.3-2.9-.7-.5 2.8-1.9-2.2-2.4 1.7.4-2.9-2.9-.2 1.9-2-2.1-2 2.9-.3.6-2.6Z" opacity=".95"/>
  <path d="M3.4 17.6c2.6-1 5.6-1.5 8.6-1.5s6 .5 8.6 1.5v2.2c-2.6-1-5.6-1.5-8.6-1.5s-6 .5-8.6 1.5v-2.2Z" opacity=".55"/>
</svg>`;

/**
 * Traffic light — the three lamps are punched out of the housing with
 * fill-rule="evenodd" so they read as lamps whatever colour sits behind them.
 * (Drawing them in white failed on the white-ink badges: white on white.)
 */
const ICON_TRAFFIC = `
<svg viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd" aria-hidden="true">
  <path d="M8.4 2.4h7.2A2.4 2.4 0 0 1 18 4.8v10.4a6 6 0 0 1-12 0V4.8a2.4 2.4 0 0 1 2.4-2.4Zm3.6 2.4a1.85 1.85 0 1 0 0 3.7 1.85 1.85 0 0 0 0-3.7Zm0 4.75a1.85 1.85 0 1 0 0 3.7 1.85 1.85 0 0 0 0-3.7Zm0 4.75a1.85 1.85 0 1 0 0 3.7 1.85 1.85 0 0 0 0-3.7Z"/>
</svg>`;

/** Construction barrier with hazard stripes */
const ICON_CONSTRUCTION = `
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path d="M2.6 6.6h18.8a1 1 0 0 1 1 1v6.2a1 1 0 0 1-1 1H2.6a1 1 0 0 1-1-1V7.6a1 1 0 0 1 1-1Z"/>
  <path d="M5.4 6.6 2.2 14.8H1.6a1 1 0 0 1-1-1v-.5l2.4-6.7h2.4Zm5.4 0-3.2 8.2H5.2L8.4 6.6h2.4Zm5.4 0-3.2 8.2h-2.4l3.2-8.2H16.2Zm5.4 0-3.2 8.2H16l3.2-8.2h2.4Z" fill="#fff" opacity=".9"/>
  <path d="M4.2 14.8h2v6.6h-2zM17.8 14.8h2v6.6h-2z"/>
</svg>`;

/* ── Palette ─────────────────────────────────────────────────────────────── */

/**
 * Per-slug colour and glyph.
 *   color — disc / chip fill
 *   ink   — glyph colour on top of it (dark on the light yellows and oranges)
 */
export const CATEGORY_STYLES = {
	ice: { color: "#3aa8dd", ink: "#08202b", icon: ICON_ICE, emoji: "🧊" },
	police: { color: "#4059d0", ink: "#ffffff", icon: ICON_POLICE, emoji: "👮‍♀️" },
	safety: { color: "#e04141", ink: "#ffffff", icon: ICON_SAFETY, emoji: "⚠️" },
	accident: { color: "#f07316", ink: "#20120a", icon: ICON_ACCIDENT, emoji: "🚨" },
	pothole: { color: "#8a6844", ink: "#ffffff", icon: ICON_POTHOLE, emoji: "🕳️" },
	traffic: { color: "#1f9d55", ink: "#ffffff", icon: ICON_TRAFFIC, emoji: "🚦" },
	construction: {
		color: "#e8b006",
		ink: "#241a02",
		icon: ICON_CONSTRUCTION,
		emoji: "🚧",
	},
};

/**
 * Título automático de un reporte: la etiqueta de su categoría con emoji.
 *
 * Los reportes ya no se titulan a mano — el título lo define la categoría
 * elegida, así la lista se lee de un vistazo y nadie tiene que redactar nada
 * con prisa.
 *
 * @param {object} category - Registro de categoría ({ slug, label })
 * @returns {string} p. ej. "Policía 👮‍♀️"
 */
export function categoryTitle(category) {
	const label = category?.label || "Reporte";
	const emoji = CATEGORY_STYLES[category?.slug]?.emoji;
	return emoji ? `${label} ${emoji}` : label;
}

/**
 * Resolve the styling for a category.
 * Falls back to the Directus ramp + Phosphor icon for slugs not in the palette,
 * so a category added in the CMS still renders (just without bespoke art).
 *
 * @param {object} category - Category record ({ slug, color_ramp, icon })
 * @returns {{ color: string, ink: string, iconSvg: string|null, phosphor: string }}
 */
export function getCategoryStyle(category) {
	const cat = category || {};
	const preset = CATEGORY_STYLES[cat.slug];

	if (preset) {
		return {
			color: preset.color,
			ink: preset.ink,
			iconSvg: preset.icon,
			phosphor: cat.icon || "map-pin",
		};
	}

	const ramp = cat.color_ramp || "accent-600";
	return {
		color: `var(--color-${ramp})`,
		ink: "#ffffff",
		iconSvg: null,
		phosphor: cat.icon || "map-pin",
	};
}

/**
 * Markup for a category glyph — bespoke SVG when we have one, Phosphor icon
 * otherwise.
 *
 * @param {object} category
 * @param {string} [className] - Extra class for the Phosphor fallback
 * @returns {string}
 */
export function categoryIconHtml(category, className = "") {
	const { iconSvg, phosphor } = getCategoryStyle(category);
	if (iconSvg) return iconSvg;
	return `<i class="ph ph-${phosphor}${className ? ` ${className}` : ""}" aria-hidden="true"></i>`;
}
